import Button from '@/src/components/atom/Button';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import Input from '@/src/components/atom/Input';
import RadioGroup from '@/src/components/atom/RadioGroup';
import BottomFixedContainer from '@/src/components/molecule/BottomFixedContainer';
import FormContent from '@/src/components/molecule/FormContent';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import SectionCard from '@/src/components/molecule/SectionCard';
import { Form, FormField } from '@/src/components/ui/form';
import { BUTTON_CONST } from '@/src/constants/buttons';
import { TOAST_CONST } from '@/src/constants/toast';
import { useToast } from '@/src/hooks/useToast';
import PATH from '@/src/routes/path';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useController, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { NAV_CONST } from '@/src/constants/nav';
import TextArea from '@/src/components/atom/TextArea';
import { usePrompt } from '@/src/hooks/usePrompt';
import { PostEntryRequestBody } from '@/src/apis/rest/entry.dtos';
import { postEntryAPI } from '@/src/apis/rest/entry';
import DateRangePicker from '@/src/components/atom/DateRangePicker';
import dayjs from 'dayjs';
import { DateRange } from 'react-day-picker';
import ImageUploadInfo from '@/src/components/molecule/ImageUploadInfo';
import ImageUpload from '@/src/components/atom/ImageUpload';
import { Media } from '@/src/models/MeidaDto';
import IconButton from '@/src/components/atom/Button/IconButton';
import Editor from '@/src/components/atom/Editor';
import EntryPreview from '@/src/pages/Entry/components/EntryPreview';
import { webviewDataInjection } from '@/src/helpers/webviewHelper';
import { inAppLinkRegex, urlRegex } from '@/src/utils/regex';
import { defaultCautions, defaultInfos } from '@/src/pages/Entry/defaultData';
import React, { useRef, useState } from 'react';
import {
  EntryWebviewTemplate,
  entryWebviewMapper,
} from '@/src/helpers/webviewHelper/entry';
import { getContentTemplatesAPI } from '@/src/apis/rest/contentTemplate';
import { APIError } from '@/src/models/common/apiError';
import useDialog, { DialogType } from '@/src/hooks/useDialog';
import { COMMON_ALERT_DIALOG_CONST } from '@/src/constants/alertDialog';
import { OPTIONS_CONST } from '@/src/constants/options';
import InfoFormSectionCard from '@/components/organism/InfoFormSectionCard/InfoFormSectionCard';
import { EntryType } from '@/src/models/EntryDto';

const BACK_PATH = PATH.ENTRY_LIST;

const formSchema = () => {
  return z
    .object({
      type: z.string(),
      startedAt: z.string().min(1),
      endedAt: z.string().min(1),
      firstServeCount: z.number().optional(),
      entryCredit: z.number(),
      isExposed: z.boolean(),
      priorityDisplayYn: z.boolean(),
      sponsorDescription: z.string().optional(),
      sponsorLink: z.string().regex(urlRegex).optional(),
      bottomButtonDisplayYn: z.boolean(),
      bottomButtonName: z.string().optional(),
      bottomButtonLink: z.union([
        z.string().regex(urlRegex).optional().nullable(),
        z.string().regex(inAppLinkRegex).optional().nullable(),
      ]),
      title: z.string().min(1),
      subTitle: z.string().optional(),
      contents: z.string().min(1),
      sponsorImageId: z.number().optional(),
      thumbnailImageId: z.number(),
      generateEntryInfos: z.array(
        z.object({ id: z.number().optional(), contents: z.string().min(1) }),
      ),
      generateEntryCautions: z.array(
        z.object({ id: z.number().optional(), contents: z.string().min(1) }),
      ),
    })
    .refine(data => {
      if (data.type === 'FIRST_SERVED') {
        return !!data.firstServeCount;
      }

      if (data.type !== 'FREE') {
        return data.entryCredit > 0;
      }

      return true;
    })
    .refine(data => {
      if (data.bottomButtonDisplayYn) {
        return data.bottomButtonLink && data.bottomButtonName;
      }

      return true;
    });
};

type FormValues = PostEntryRequestBody & {
  startedDateRange: DateRange;
  thumbnailImage?: Media;
  sponsorImage?: Media;
};

const DEFAULT_VALUES: Partial<FormValues> = {
  title: '',
  subTitle: '',
  contents: '',
  startedDateRange: {
    from: undefined,
    to: undefined,
  },
  isExposed: true,
  type: 'NORMAL',
  priorityDisplayYn: true,
  bottomButtonDisplayYn: false,
  generateEntryInfos: defaultInfos,
  generateEntryCautions: defaultCautions,
};

export default function EntryCreatePage() {
  // util
  const navigate = useNavigate();
  const { toast } = useToast();

  // form
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema()),
    defaultValues: DEFAULT_VALUES,
    reValidateMode: 'onSubmit',
  });
  const { field: startedAtField } = useController({
    name: 'startedAt',
    control: form.control,
  });
  const { field: endedAtField } = useController({
    name: 'endedAt',
    control: form.control,
  });
  const { field: bottomButtonNameField } = useController({
    name: 'bottomButtonName',
    control: form.control,
  });
  const { field: bottomButtonLinkField } = useController({
    name: 'bottomButtonLink',
    control: form.control,
  });
  const { field: entryCreditFiled } = useController({
    name: 'entryCredit',
    control: form.control,
  });

  // query
  const { data: contentTemplateData } = useQuery({
    queryKey: ['content-template', 'ENTRY'],
    queryFn: () => {
      return getContentTemplatesAPI({ type: 'ENTRY' });
    },
  });

  // state
  const {
    Dialog: PriorityErrorDialog,
    openDialog: openPriorityErrorDialog,
    isOpen: isPriorityErrorOpen,
    closeDialog: closePriorityErrorDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });
  const [isSponsorLinkError, setIsSponsorLinkError] = useState(false);
  const [isBottomLinkError, setIsBottomLinkError] = useState(false);

  const contentsMarkup = webviewDataInjection<EntryWebviewTemplate>(
    contentTemplateData?.rows?.[0]?.markup || '',
    entryWebviewMapper({
      ...form.watch(),
      entryInfos: form.watch('generateEntryInfos'),
      entryCautions: form.watch('generateEntryCautions'),
    }),
  );
  const {
    Dialog: SaveDialog,
    isOpen: isSaveOpen,
    openDialog: openSaveDialog,
    closeDialog: closeSaveDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });
  const formRef = useRef<HTMLFormElement>(null);

  // mutation
  const createMutation = useMutation({ mutationFn: postEntryAPI });

  // event
  const handleGoBack = () => {
    navigate(-1);
  };

  const handleSubmit = (values: PostEntryRequestBody) => {
    const newValues = {
      ...values,
      firstServeCount:
        typeof values?.firstServeCount === 'number'
          ? values.firstServeCount
          : 0,
    };

    createMutation.mutate(
      { ...newValues },
      {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: TOAST_CONST.saveSuccess.title,
            description: TOAST_CONST.saveSuccess.description,
          });

          navigate(BACK_PATH);
        },
        onError: e => {
          const { error } = e as unknown as APIError;

          if (error === 'CANNOT_PRIORITY_EXPOSE_OVER_THAN_FIVE') {
            openPriorityErrorDialog();
          } else {
            toast({
              variant: 'error',
              title: TOAST_CONST.error.title,
              description: e.message,
            });
          }
        },
      },
    );
  };

  // render
  const getDateDisabledMatcher = (date: Date) => {
    if (dayjs().isAfter(date, 'D')) return true;

    return false;
  };
  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Button
            className="w-max"
            buttonSize="medium"
            buttonColor="outline"
            onClick={handleGoBack}
          >
            <div className="flex items-center gap-x-2">
              <LucideIcon name="ChevronLeft" size={16} />
              {BUTTON_CONST.backToScreen}
            </div>
          </Button>
          <Button
            disabled={!form.formState.isValid}
            type="button"
            onClick={openSaveDialog}
            className="w-40"
            buttonSize="medium"
            isLoading={createMutation.isPending}
          >
            <div className="flex items-center gap-x-2">{BUTTON_CONST.save}</div>
          </Button>
        </div>
      </BottomFixedContainer>
    );
  };

  // usePrompt
  usePrompt(form.formState.isDirty && !form.formState.isSubmitted);

  return (
    <>
      <MainContentTitle
        breadcrumbList={[NAV_CONST.entryManagement, NAV_CONST.entryCreate]}
        pageTitle={NAV_CONST.entryCreate}
      />
      <Form {...form}>
        <form
          ref={formRef}
          className="relative mb-[100px] grid grid-cols-[1fr_1fr] gap-4"
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <SectionCard cardTitle="응모하기 기본 정보" className="col-span-2">
            <div className="grid grid-cols-[1fr_auto_1fr] gap-6">
              <div className="flex flex-col gap-6">
                <FormField
                  control={form.control}
                  name="startedDateRange"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent isRequired label="응모 기간" error={error}>
                        <DateRangePicker
                          {...field}
                          onChange={e => {
                            field.onChange(e);

                            startedAtField.onChange(
                              e?.from
                                ? dayjs(e?.from).format('YYYY-MM-DD')
                                : '',
                            );
                            endedAtField.onChange(
                              e?.to ? dayjs(e?.to).format('YYYY-MM-DD') : '',
                            );
                          }}
                          dateDisabledMatcher={getDateDisabledMatcher}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name="isExposed"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent label="노출 여부" error={error}>
                        <RadioGroup
                          optionList={[
                            { label: OPTIONS_CONST.exposed, value: 'true' },
                            { label: OPTIONS_CONST.notExposed, value: 'false' },
                          ]}
                          rootProps={{
                            value: field.value ? 'true' : 'false',
                            onValueChange: e => field.onChange(e === 'true'),
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name="priorityDisplayYn"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent label="우선 노출 여부" error={error}>
                        <RadioGroup
                          optionList={[
                            { label: OPTIONS_CONST.applied, value: 'true' },
                            { label: OPTIONS_CONST.notApplied, value: 'false' },
                          ]}
                          rootProps={{
                            value: field.value ? 'true' : 'false',
                            onValueChange: e => field.onChange(e === 'true'),
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name="thumbnailImageId"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        isRequired
                        label="썸네일 이미지"
                        defaultMessage={
                          <ImageUploadInfo widthSize={375} heightSize={375} />
                        }
                        error={error}
                      >
                        <ImageUpload
                          width={128}
                          height={128}
                          widthText={375}
                          heightText={375}
                          maxImageSizeMb={5}
                          imageClassName="object-cover"
                          onChange={e => {
                            field.onChange(e.id);
                            form.setValue('thumbnailImage', e);
                          }}
                          topAccessary={({ handleEdit, handleRemove }) => {
                            return (
                              <>
                                <div className="flex gap-x-1">
                                  <IconButton
                                    buttonSize="xSmall"
                                    buttonColor="outline"
                                    lucideIconProps={{ name: 'Pencil' }}
                                    onClick={() => {
                                      handleEdit();
                                    }}
                                  />
                                  <IconButton
                                    buttonSize="xSmall"
                                    buttonColor="outline"
                                    lucideIconProps={{ name: 'X' }}
                                    onClick={() => {
                                      handleRemove();
                                      field.onChange(undefined);
                                      form.setValue(
                                        'thumbnailImage',
                                        undefined,
                                      );
                                    }}
                                  />
                                </div>
                              </>
                            );
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
              </div>
              <div className="border-r border-r-gray1" />
              <div className="flex flex-col gap-6">
                <FormField
                  control={form.control}
                  name="type"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent label="응모 유형" error={error}>
                        <RadioGroup
                          optionList={[
                            {
                              label: '매일 참여',
                              value: EntryType.DAILY,
                            },
                            {
                              label: '이벤트',
                              value: EntryType.NORMAL,
                            },
                            {
                              label: '선착순',
                              value: EntryType.FIRST_SERVED,
                            },
                            {
                              label: '무료',
                              value: EntryType.FREE,
                            },
                          ]}
                          rootProps={{
                            value: field.value,
                            onValueChange: e => {
                              field.onChange(e);
                              form.setValue('firstServeCount', undefined);

                              if (e === 'FREE') {
                                entryCreditFiled.onChange(0);
                              } else {
                                entryCreditFiled.onChange(undefined);
                              }
                            },
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name="firstServeCount"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent label="선착순 응모 인원" error={error}>
                        <Input
                          type="number"
                          min={0}
                          onClear={() => field.onChange(undefined)}
                          disabled={!(form.watch('type') === 'FIRST_SERVED')}
                          placeholder="응모 인원을 숫자로 입력해주세요 (응모 인원이 채워질 경우 응모되지 않습니다)"
                          {...field}
                          value={field.value || undefined}
                          onChange={e =>
                            field.onChange(
                              e.target.value
                                ? Number(e.target.value)
                                : undefined,
                            )
                          }
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name="entryCredit"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        label="1회 응모 크레딧"
                        isRequired
                        error={error}
                      >
                        <Input
                          type="number"
                          min={0}
                          onClear={() => field.onChange(undefined)}
                          placeholder={
                            "사용 크레딧를 입력해주세요 ('0크레딧'으로 입력할 경우 무료 응모로 게시됩니다.)"
                          }
                          disabled={form.watch('type') === 'FREE'}
                          {...field}
                          onChange={e =>
                            field.onChange(
                              e.target.value
                                ? Number(e.target.value)
                                : undefined,
                            )
                          }
                        />
                      </FormContent>
                    );
                  }}
                />

                {/* ㅅㄷㄴㅅ */}
              </div>
            </div>
          </SectionCard>
          <SectionCard
            cardTitle="응모하기 선택 정보"
            className="col-span-2"
            isCollapsible
            defaultOpen={false}
          >
            <div className="grid grid-cols-[1fr_auto_1fr] gap-6">
              <div className="flex flex-col gap-6">
                {' '}
                <FormField
                  control={form.control}
                  name="sponsorImageId"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        label="제휴사 이미지"
                        defaultMessage={
                          <ImageUploadInfo widthSize={375} heightSize={375} />
                        }
                        error={error}
                      >
                        <ImageUpload
                          width={128}
                          height={128}
                          widthText={375}
                          heightText={375}
                          maxImageSizeMb={5}
                          imageClassName="object-cover"
                          onChange={e => {
                            field.onChange(e.id);
                            form.setValue('sponsorImage', e);
                          }}
                          topAccessary={({ handleEdit, handleRemove }) => {
                            return (
                              <>
                                <div className="flex gap-x-1">
                                  <IconButton
                                    buttonSize="xSmall"
                                    buttonColor="outline"
                                    lucideIconProps={{ name: 'Pencil' }}
                                    onClick={() => {
                                      handleEdit();
                                    }}
                                  />
                                  <IconButton
                                    buttonSize="xSmall"
                                    buttonColor="outline"
                                    lucideIconProps={{ name: 'X' }}
                                    onClick={() => {
                                      handleRemove();
                                      field.onChange(undefined);
                                      form.setValue('sponsorImage', undefined);
                                    }}
                                  />
                                </div>
                              </>
                            );
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name="sponsorDescription"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent label="제휴사 설명" error={error}>
                        <TextArea
                          placeholder="제휴사에 대해 간략한 설명을 적을 수 있어요"
                          className="h-[100px]"
                          {...field}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name="sponsorLink"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        label="제휴사 홈페이지 링크"
                        error={
                          isSponsorLinkError
                            ? {
                                type: 'pattern',
                                message:
                                  'url 형식을 확인해주세요 (https://example.com)',
                              }
                            : undefined
                        }
                      >
                        <Input
                          {...field}
                          onChange={e => {
                            field.onChange(e.target.value || undefined);
                            if (e.target.value) {
                              setIsSponsorLinkError(
                                !urlRegex.test(e.target.value),
                              );
                            } else {
                              setIsSponsorLinkError(false);
                            }
                          }}
                          onClear={() => {
                            field.onChange('');
                            setIsSponsorLinkError(false);
                            setTimeout(() => {
                              field.onChange(undefined);
                            }, 0);
                          }}
                          placeholder="링크를 입력해주세요 (https://example.com)"
                        />
                      </FormContent>
                    );
                  }}
                />
              </div>
              <div className="border-r border-r-gray1" />
              <div className="flex flex-col gap-6">
                <FormField
                  control={form.control}
                  name="bottomButtonDisplayYn"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent label="하단 버튼 노출 여부" error={error}>
                        <RadioGroup
                          optionList={[
                            { label: '선택 안함', value: 'false' },
                            { label: '버튼 생성', value: 'true' },
                          ]}
                          rootProps={{
                            value: field.value ? 'true' : 'false',
                            onValueChange: e => {
                              if (e === 'false') {
                                bottomButtonNameField.onChange('');
                                bottomButtonLinkField.onChange('');
                                setTimeout(() => {
                                  bottomButtonNameField.onChange(undefined);
                                  bottomButtonLinkField.onChange(undefined);
                                }, 0);
                              }
                              field.onChange(e === 'true');
                            },
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name="bottomButtonName"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent label="하단 버튼 명" error={error}>
                        <Input
                          maxLength={20}
                          disabled={!form.watch('bottomButtonDisplayYn')}
                          onClear={() => field.onChange(undefined)}
                          placeholder="하단 버튼 이름을 입력해주세요 (최대 20글자)"
                          {...field}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name="bottomButtonLink"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        label="하단 버튼 링크"
                        error={
                          isBottomLinkError
                            ? {
                                type: 'pattern',
                                message:
                                  '하단 버튼 링크 형식을 확인해주세요 (https://example.com 또는 /app/*)',
                              }
                            : undefined
                        }
                      >
                        <Input
                          disabled={!form.watch('bottomButtonDisplayYn')}
                          {...field}
                          onChange={e => {
                            field.onChange(e.target.value || undefined);

                            if (e.target.value) {
                              setIsBottomLinkError(
                                !urlRegex.test(e.target.value) &&
                                  !inAppLinkRegex.test(e.target.value),
                              );
                            } else {
                              setIsBottomLinkError(false);
                            }
                          }}
                          onClear={() => {
                            field.onChange('');
                            setIsBottomLinkError(false);
                            setTimeout(() => {
                              field.onChange(undefined);
                            }, 0);
                          }}
                          placeholder={
                            '링크를 입력해주세요 (https://example.com 또는 /app/*)'
                          }
                        />
                      </FormContent>
                    );
                  }}
                />
              </div>
            </div>
          </SectionCard>

          <SectionCard cardTitle="응모하기 내용" className="col-span-2">
            <div className="grid grid-cols-[1fr_auto_1fr] gap-6">
              <div className="col-span-3 flex gap-6">
                <div className="flex flex-1 flex-col gap-6">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent isRequired label="제목" error={error}>
                          <Input
                            onClear={() => field.onChange(undefined)}
                            placeholder="제목을 입력해주세요"
                            {...field}
                          />
                        </FormContent>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="subTitle"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent label="서브 제목" error={error}>
                          <Input
                            onClear={() => field.onChange(undefined)}
                            placeholder="서브 제목을 입력해주세요"
                            {...field}
                          />
                        </FormContent>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="contents"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent
                          className="col-span-3"
                          label="내용"
                          isRequired
                          error={error}
                        >
                          <div className="flex gap-3">
                            <div className="flex-1">
                              <Editor
                                placeholder="내용을 입력하세요"
                                height={650}
                                value={field.value || ''}
                                onChange={field.onChange}
                              />
                            </div>
                          </div>
                        </FormContent>
                      );
                    }}
                  />
                </div>
                <EntryPreview contentsMarkup={contentsMarkup} />
              </div>
            </div>
          </SectionCard>
          <InfoFormSectionCard
            cardTitle="안내사항 영역"
            form={form}
            field="generateEntryInfos"
          />
          <InfoFormSectionCard
            cardTitle="유의사항 영역"
            form={form}
            field="generateEntryCautions"
            buttonText="유의사항 추가하기"
          />
          {renderBottomFixedContent()}
        </form>
      </Form>

      {/* Modal */}
      <PriorityErrorDialog
        open={isPriorityErrorOpen}
        onOpenChange={closePriorityErrorDialog}
        onOk={closePriorityErrorDialog}
        title="우선 노출 여부 지정 불가"
        cancelButtonProps={{
          className: 'hidden',
        }}
      >
        이미 해당 응모 기간 내 우선노출 지정 개수가 5개 있습니다.
        <br />
        다른 응모 상품을 확인 후 재시도 해주세요.
      </PriorityErrorDialog>
      {isSaveOpen && (
        <SaveDialog
          open={isSaveOpen}
          onOk={() => formRef?.current?.requestSubmit()}
          onOpenChange={e => {
            if (e) {
              openSaveDialog();
            } else {
              closeSaveDialog();
            }
          }}
          title={COMMON_ALERT_DIALOG_CONST.save}
          isLoading={createMutation.isPending}
        />
      )}
    </>
  );
}
